import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from './AssignMentorDialog';

// ─────────────────────────── dialogs ───────────────────────────
export function PlacementDialog({ app, onClose, onDone }) {
  const prefs = [app.priority1, app.priority2].filter(Boolean);
  const current = app.suggested?.id || '';
  const [choice, setChoice] = useState(app.allocation_status === 'waitlisted' ? 'waitlist' : current);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (action, opportunityId) => {
    setSaving(true);
    try {
      const r = await api.post(`/coordinator/internship-applications/${app.id}/placement`,
        { action, opportunity_id: opportunityId || null, reason: reason.trim() || null });
      toast.success(action === 'clear' ? 'Back to the automatic suggestion' : 'Placement updated');
      onDone(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update this placement'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="placement-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">{app.student_name}</DialogTitle>
          <DialogDescription className="text-xs">
            Grade {app.grade} · applied {formatDay(app.first_submitted_at?.slice(0, 10))}. A student can only be
            placed in one of their own preferences, and only one placement per programme.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {prefs.map((p, i) => (
            <button key={p.id} onClick={() => setChoice(p.id)} className={`w-full text-left px-3 py-2.5 rounded-xl border transition-colors ${choice === p.id ? 'border-[#F97D6A] bg-[#FEF0EC]' : 'border-gray-200 hover:border-[#9CA3AF]'}`} data-testid={`placement-option-${p.id}`}>
              <p className="text-sm font-medium text-[#1F2937]">Priority {i + 1} — {p.title}</p>
              <p className="text-[11px] text-[#6B7280]">{p.company_name} · {formatDay(p.start_date)} · {p.positions ?? ''} {p.positions ? 'places' : ''}</p>
            </button>
          ))}
          <button onClick={() => setChoice('waitlist')} className={`w-full text-left px-3 py-2.5 rounded-xl border transition-colors ${choice === 'waitlist' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-[#9CA3AF]'}`} data-testid="placement-option-waitlist">
            <p className="text-sm font-medium text-[#1F2937]">Keep on the waitlist</p>
            <p className="text-[11px] text-[#6B7280]">Frees the seat for the next student in the queue.</p>
          </button>
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Reason for this change *</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Recorded with your name for the school's records" className="mt-1.5 rounded-xl border-gray-200 min-h-[56px] resize-none text-sm" data-testid="placement-reason" />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => save('clear')} disabled={saving} className="rounded-xl text-sm text-[#6B7280]" data-testid="placement-clear-btn">
            Use automatic suggestion
          </Button>
          <Button onClick={() => (choice === 'waitlist' ? save('waitlist') : save('assign', choice))} disabled={saving || !choice || !reason.trim()} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="placement-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save placement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SendDialog({ rows, programId, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const byCompany = rows.reduce((acc, r) => {
    const key = r.suggested?.company_name || 'Company';
    (acc[key] = acc[key] || []).push(r);
    return acc;
  }, {});

  const send = async () => {
    setSaving(true);
    try {
      const r = await api.post('/coordinator/internship-proposals', {
        program_id: programId, application_ids: rows.map(x => x.id) });
      if (r.data.sent > 0) toast.success(`${r.data.sent} student${r.data.sent === 1 ? '' : 's'} sent for company confirmation`);
      (r.data.skipped || []).forEach(sk => toast.error(`${sk.student_name || 'A student'}: ${sk.reason}`));
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send these students'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="send-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">Send {rows.length} student{rows.length === 1 ? '' : 's'} for confirmation</DialogTitle>
          <DialogDescription className="text-xs">
            Each company sees only the students proposed to them, with their resume. They accept or decline — you
            still make the final placement.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {Object.entries(byCompany).map(([company, list]) => (
            <div key={company} data-testid={`send-group-${company}`}>
              <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-1">{company}</p>
              <ul className="divide-y divide-[#F3F4F6] border border-[#F3F4F6] rounded-xl">
                {list.map(r => (
                  <li key={r.id} className="px-3 py-1.5 flex items-center justify-between gap-2">
                    <span className="text-sm text-[#1F2937]">{r.student_name} <span className="text-[#9CA3AF]">· grade {r.grade}</span></span>
                    <span className="text-[10px] text-[#9CA3AF]">{r.suggested?.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={send} disabled={saving} className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-xl font-semibold" data-testid="confirm-send-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Send for confirmation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function WithdrawDialog({ row, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/coordinator/internship-proposals/${row.proposal.id}/withdraw`, { reason: reason.trim() });
      toast.success('Proposal withdrawn');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not withdraw this proposal'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="withdraw-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">Withdraw {row.student_name} from {row.proposal?.company_name}?</DialogTitle>
          <DialogDescription className="text-xs">
            {row.proposal?.opportunity_title} · the company can no longer act on this student, and the history is kept.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs font-medium text-[#1F2937]">Reason *</Label>
          <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Recorded with your name for the school's records" className="mt-1.5 rounded-xl border-gray-200 min-h-[64px] resize-none text-sm" data-testid="withdraw-reason" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={submit} disabled={saving || !reason.trim()} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="confirm-withdraw-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Withdraw proposal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FinalizeDialog({ board, rows, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const ready = rows.filter(r => r.can_finalize);
  const schoolMode = board.placement_mode === 'school_finalizes';

  const finalize = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/coordinator/internship-programs/${board.program.id}/finalize-placements`,
        { confirm: true, application_ids: ready.map(x => x.id) });
      const { sent, failed } = r.data.emails;
      const note = failed > 0 ? `${sent} parent email(s) sent, ${failed} failed — retry from the board`
        : `${sent} parent email(s) sent`;
      (failed > 0 ? toast.warning : toast.success)(
        `${r.data.finalized} placement${r.data.finalized === 1 ? '' : 's'} confirmed · ${note}`);
      (r.data.skipped || []).forEach(sk => toast.error(`${sk.student_name}: ${sk.reason}`));
      onDone(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not finalise these placements'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="finalize-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">Finalise {ready.length} placement{ready.length === 1 ? '' : 's'}</DialogTitle>
          <DialogDescription className="text-xs">
            {schoolMode
              ? `${board.program.name} · the school confirms these placements. Send each company its final list once you are done.`
              : `${board.program.name} · only students a company has accepted can be finalised. Students still waiting on other companies are unaffected.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {ready.length === 0 ? (
            <p className="text-sm text-[#6B7280] flex items-center gap-1.5" data-testid="finalize-blocked-note">
              <Clock className="h-3.5 w-3.5 text-[#9CA3AF]" />
              {board.program.applications_open
                ? `Parents can still edit until ${formatDay(board.program.application_deadline)}. Finalising unlocks after the deadline.`
                : schoolMode
                  ? 'No student has an allocated internship yet. Refresh the suggestions or adjust a placement first.'
                  : 'No student has been accepted by a company yet.'}
            </p>
          ) : (
            <ul className="divide-y divide-[#F3F4F6] border border-[#F3F4F6] rounded-xl" data-testid="finalize-list">
              {ready.map(r => (
                <li key={r.id} className="px-3 py-2" data-testid={`finalize-row-${r.id}`}>
                  <p className="text-xs font-medium text-[#1F2937]">{r.student_name} <span className="text-[#9CA3AF]">· grade {r.grade}</span></p>
                  <p className="text-[11px] text-[#6B7280]">
                    {schoolMode
                      ? `${r.suggested?.title || ''} · ${r.suggested?.company_name || ''}`
                      : `${r.proposal?.opportunity_title || ''} · ${r.proposal?.company_name || ''}`}
                  </p>
                </li>
              ))}
            </ul>
          )}
          {ready.length > 0 && (
            <label className="flex items-start gap-2 bg-[#FEF0EC] rounded-xl px-3 py-2.5 cursor-pointer">
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-0.5 accent-[#F97D6A]" data-testid="finalize-confirm-checkbox" />
              <span className="text-sm text-[#4B5563] leading-relaxed">
                I confirm these placements. Each parent will be emailed their child's confirmed internship, and the
                placement is locked against further reallocation.
              </span>
            </label>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Close</Button>
          {ready.length > 0 && (
            <Button onClick={finalize} disabled={saving || !confirmed} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="finalize-confirm-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Confirm & notify parents
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReplacementsDialog({ programId, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    api.get(`/coordinator/internship-programs/${programId}/replacements`)
      .then(r => setData(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || 'Could not load replacements'); closeRef.current(); });
  }, [programId]);

  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  const send = async () => {
    setSaving(true);
    try {
      const r = await api.post('/coordinator/internship-proposals', { program_id: programId, application_ids: picked });
      if (r.data.sent > 0) toast.success(`${r.data.sent} replacement${r.data.sent === 1 ? '' : 's'} sent for company confirmation`);
      (r.data.skipped || []).forEach(sk => toast.error(`${sk.student_name || 'A student'}: ${sk.reason}`));
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send these students'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="replacements-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">Review replacements</DialogTitle>
          <DialogDescription className="text-xs">
            Seats still open after company decisions, with the next students in Priority 1 → Priority 2 and
            application order. Nothing is sent until you choose.
          </DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#9CA3AF]" /></div> : (
          <div className="space-y-3">
            {data.rejected.length > 0 && (
              <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="replacements-rejected">
                <p className="text-xs font-semibold text-red-600">{data.rejected.length} student{data.rejected.length === 1 ? '' : 's'} declined by a company</p>
                {data.rejected.map(r => (
                  <p key={r.id} className="text-[11px] text-[#6B7280] mt-0.5">
                    {r.student_name} — {r.proposal?.company_name}{r.proposal?.reason ? ` · “${r.proposal.reason}”` : ''}
                  </p>
                ))}
                <p className="text-[10px] text-[#9CA3AF] mt-1">They keep their original application time and are back in the queue for their other preference.</p>
              </div>
            )}
            {data.seats.length === 0 && (
              <p className="text-sm text-[#9CA3AF] text-center py-6" data-testid="replacements-empty">Every seat is either finalised or awaiting a company decision.</p>
            )}
            {data.seats.map(seat => (
              <div key={seat.opportunity_id} className="border border-[#E5E7EB] rounded-xl px-3 py-2.5" data-testid={`replacement-seat-${seat.opportunity_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-[#1F2937]">{seat.title}</p>
                    <p className="text-[11px] text-[#6B7280]">{seat.company_name}</p>
                  </div>
                  <p className="text-[11px] text-[#4B5563]"><b>{seat.seats_available}</b> of {seat.positions} available · {seat.finalized} finalized · {seat.accepted} accepted · {seat.awaiting} awaiting</p>
                </div>
                {seat.recommended.length === 0 && seat.other_candidates.length === 0 ? (
                  <p className="text-[11px] text-[#9CA3AF] mt-1.5">No eligible student is waiting for this internship.</p>
                ) : (
                  <ul className="mt-2 divide-y divide-[#F7F8F9]">
                    {[...seat.recommended, ...seat.other_candidates].map(c => (
                      <li key={`${seat.opportunity_id}-${c.id}`} className="py-1.5 flex items-center justify-between gap-3" data-testid={`replacement-candidate-${c.id}`}>
                        <label className="flex items-center gap-2 min-w-0 cursor-pointer">
                          <input type="checkbox" checked={picked.includes(c.id)} onChange={() => toggle(c.id)}
                            disabled={(c.suggested || {}).id !== seat.opportunity_id}
                            className="accent-[#F97D6A]" data-testid={`replacement-pick-${c.id}`} />
                          <span className="text-sm text-[#1F2937] truncate">{c.student_name}</span>
                          <Badge className={`border-0 text-[10px] ${c.preference === 1 ? 'bg-[#FEF0EC] text-[#F97D6A]' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>P{c.preference}</Badge>
                          <span className="text-[10px] text-[#9CA3AF]">applied {formatDay(c.first_submitted_at?.slice(0, 10))}</span>
                        </label>
                        <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap">
                          {(c.suggested || {}).id === seat.opportunity_id ? 'Recommended' : `Currently ${c.suggested?.title || 'unallocated'}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Close</Button>
          <Button onClick={send} disabled={saving || picked.length === 0} className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-xl font-semibold disabled:opacity-40" data-testid="send-replacements-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Send {picked.length || ''} for confirmation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CloseAllocationDialog({ board, onClose, onDone }) {
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const c = board.closure;

  const close = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/coordinator/internship-programs/${board.program.id}/close-allocation`, { confirm: true });
      const n = r.data.not_selected;
      const { failed } = r.data.emails;
      const who = `${n} ${n === 1 ? 'family' : 'families'}`;
      (failed > 0 ? toast.warning : toast.success)(
        failed > 0 ? `Allocation closed · ${failed} email(s) failed — retry from the board`
          : `Allocation closed · ${who} notified`);
      onDone(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not close the allocation'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="close-allocation-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">Close allocation for {board.program.name}</DialogTitle>
          <DialogDescription className="text-xs">
            Finalised placements stay exactly as they are. Everyone still unplaced is marked not selected and their
            family receives a short, respectful note.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <div className="grid grid-cols-3 gap-2">
            {[['Finalized', board.summary.finalized], ['Awaiting company', board.summary.company_awaiting],
              ['Unplaced', board.summary.unplaced]].map(([k, v]) => (
              <div key={k} className="bg-[#FAFBFC] rounded-xl px-3 py-2 text-center">
                <p className="text-lg font-bold text-[#1F2937]" data-testid={`closure-stat-${k.toLowerCase().replace(/ /g, '-')}`}>{v}</p>
                <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">{k}</p>
              </div>
            ))}
          </div>
          {c.awaiting_company.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="closure-awaiting">
              <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Resolve these before closing</p>
              {c.awaiting_company.map(a => (
                <p key={a.application_id} className="text-[11px] text-amber-700 mt-0.5">{a.student_name} — awaiting {a.company_name}</p>
              ))}
              <p className="text-[10px] text-amber-700 mt-1">Wait for the reply, or withdraw the proposal from the student's row.</p>
            </div>
          )}
          {c.replaceable.length > 0 && (
            <p className="text-[11px] text-[#6B7280]" data-testid="closure-replaceable">
              {c.replaceable.length} student{c.replaceable.length === 1 ? ' is' : 's are'} still eligible for a replacement placement.
            </p>
          )}
          {c.unfilled_seats.length > 0 && (
            <div data-testid="closure-unfilled">
              <p className="text-[11px] uppercase tracking-wide text-[#9CA3AF] mb-1">Seats that will stay unfilled</p>
              <ul className="divide-y divide-[#F3F4F6] border border-[#F3F4F6] rounded-xl">
                {c.unfilled_seats.map(o => (
                  <li key={o.id} className="px-3 py-1.5 flex items-center justify-between gap-2">
                    <span className="text-sm text-[#1F2937] truncate">{o.title} <span className="text-[#9CA3AF]">· {o.company_name}</span></span>
                    <span className="text-[11px] text-[#4B5563]">{o.seats_available} left</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {c.can_close && (
            <label className="flex items-start gap-2 bg-[#FEF0EC] rounded-xl px-3 py-2.5 cursor-pointer">
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-0.5 accent-[#F97D6A]" data-testid="close-confirm-checkbox" />
              <span className="text-sm text-[#4B5563] leading-relaxed">
                I understand this closes the round. No further allocation changes are possible and unplaced families
                will be notified.
              </span>
            </label>
          )}
          {!c.can_close && board.program.applications_open && (
            <p className="text-sm text-[#6B7280] flex items-center gap-1.5" data-testid="close-blocked-note">
              <Clock className="h-3.5 w-3.5 text-[#9CA3AF]" />Allocation can only be closed after the application deadline.
            </p>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={close} disabled={saving || !confirmed || !c.can_close} className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-xl font-semibold disabled:opacity-40" data-testid="confirm-close-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}Close allocation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** School-finalises mode: each company receives only its own confirmed students. */
export function FinalListsDialog({ board, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const lists = board.final_lists || [];
  const pending = lists.filter(l => l.status !== 'sent' || l.sent_students !== l.students);
  const failed = lists.filter(l => l.status === 'failed');
  const resend = pending.length === 0;

  const send = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/coordinator/internship-programs/${board.program.id}/send-final-lists`,
        { resend });
      const { final_lists_sent: sent, final_lists_failed: bad, final_lists_skipped: skip } = r.data;
      (bad > 0 ? toast.warning : toast.success)(
        `${sent} company list${sent === 1 ? '' : 's'} sent`
        + (bad ? ` · ${bad} failed, retry from here` : '')
        + (skip ? ` · ${skip} unchanged and skipped` : ''));
      onDone(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send the final lists'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="final-lists-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">Send final lists</DialogTitle>
          <DialogDescription className="text-xs">
            {board.program.name} · each company is emailed only the students confirmed for its own internship.
          </DialogDescription>
        </DialogHeader>
        {lists.length === 0 ? (
          <p className="text-sm text-[#6B7280]" data-testid="final-lists-empty">
            Finalise at least one placement first.
          </p>
        ) : (
          <ul className="divide-y divide-[#F3F4F6] border border-[#F3F4F6] rounded-xl" data-testid="final-lists-rows">
            {lists.map(l => (
              <li key={l.id} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`final-list-${l.id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#1F2937] truncate">{l.company_name}</p>
                  <p className="text-[11px] text-[#6B7280] truncate">{l.title} · {l.students} student{l.students === 1 ? '' : 's'}</p>
                  {l.error && <p className="text-[10px] text-red-500">{l.error}</p>}
                </div>
                {l.status === 'sent' && l.sent_students === l.students ? (
                  <span className="text-[10px] text-emerald-600 inline-flex items-center gap-1 shrink-0">
                    <CheckCircle2 className="h-3 w-3" />Sent {formatDay((l.sent_at || '').slice(0, 10))}
                  </span>
                ) : l.status === 'failed' ? (
                  <span className="text-[10px] text-red-500 shrink-0">Failed — will retry</span>
                ) : (
                  <span className="text-[10px] text-[#9CA3AF] shrink-0">
                    {l.status === 'sent' ? 'List changed — will resend' : 'Not sent yet'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {lists.length > 0 && (
          <p className="text-[11px] text-[#6B7280] leading-relaxed">
            {resend
              ? 'Every company already has its current list. Sending again emails them all a second time.'
              : `${pending.length} compan${pending.length === 1 ? 'y' : 'ies'} will be emailed now${failed.length ? `, including ${failed.length} earlier failure${failed.length === 1 ? '' : 's'}` : ''}. Companies whose list has not changed are skipped.`}
          </p>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Close</Button>
          {lists.length > 0 && (
            <Button onClick={send} disabled={saving} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="confirm-final-lists-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {resend ? 'Send again' : `Send ${pending.length} list${pending.length === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
